import apiClient from '../client';

// ==================== Types ====================

export interface PatientContext {
  id: string;
  name: string;
  birthDate: string;
  gender: string;
  age: number;
  phone?: string;
  activeProblems?: ProblemInfo[];
  activeMedications?: MedicationInfo[];
  allergies?: AllergyInfo[];
  currentRiskLevel?: string;
  riskFactors?: string[];
  careLines?: CareLineInfo[];
  recentQuestionnaires?: QuestionnaireInfo[];
}

export interface ProblemInfo {
  cidCode: string;
  description: string;
  status: string;
  onsetDate?: string;
}

export interface MedicationInfo {
  name: string;
  dosage: string;
  frequency: string;
  status: string;
}

export interface AllergyInfo {
  allergen: string;
  severity?: string;
  reaction?: string;
}

export interface CareLineInfo {
  id: string;
  code: string;
  name: string;
  currentPhase?: string;
}

export interface QuestionnaireInfo {
  id: string;
  code: string;
  name: string;
  score?: number;
  status: string;
  answeredAt: string;
}

// ==================== Luna Types ====================

export interface LunaChatRequest {
  message: string;
  conversationId?: string;
}

export interface LunaChatResponse {
  conversationId: string;
  message: string;
  suggestions?: string[];
  actions?: Record<string, unknown>[];
  sentiment?: string;
  intent?: string;
  requiresHumanReview: boolean;
  alertTriggered: boolean;
  alertMessage?: string;
}

export interface LunaCheckInResponse {
  message: string;
  suggestedQuestions?: string[];
  priority: string;
}

export interface PatientSummaryResponse {
  patientId: string;
  summary: string;
  generatedAt: string;
  riskLevel?: string;
}

// ==================== Propensity Types ====================

export type PropensityLevel = 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH';

export interface PropensityScoreResponse {
  patientId: string;
  score: number;
  level: PropensityLevel;
  factors?: PropensityFactor[];
  confidence: number;
  calculatedAt: string;
  validUntil?: string;
  recommendations?: string[];
}

export interface PropensityFactor {
  code: string;
  name: string;
  weight: number;
  value: number;
  contribution: number;
  description?: string;
}

// ==================== Prediction Types ====================

export type PredictionRiskLevel = 'VERY_LOW' | 'LOW' | 'MODERATE' | 'HIGH' | 'VERY_HIGH';
export type RiskTrend = 'IMPROVING' | 'STABLE' | 'WORSENING';

export interface HospitalizationPrediction {
  patientId: string;
  riskProbability: number;
  riskLevel: PredictionRiskLevel;
  horizonDays: number;
  predictedCauses?: string[];
  riskFactors?: RiskFactor[];
  trend?: RiskTrend;
  trendChangePercent?: number;
  confidence: number;
  calculatedAt: string;
  recommendations?: string[];
  preventiveActions?: Record<string, unknown>[];
}

export interface ChurnPrediction {
  patientId: string;
  churnProbability: number;
  riskLevel: PredictionRiskLevel;
  horizonDays: number;
  churnFactors?: RiskFactor[];
  engagementScore: number;
  lastMeaningfulInteraction?: string;
  daysSinceInteraction?: number;
  confidence: number;
  calculatedAt: string;
  retentionRecommendations?: string[];
}

export interface RiskFactor {
  code: string;
  name: string;
  category: string;
  impact: number;
  value: unknown;
  description?: string;
}

export interface AllPredictionsResponse {
  patientId: string;
  hospitalization: HospitalizationPrediction;
  churn: ChurnPrediction;
  calculatedAt?: string;
}

export interface AIHealthResponse {
  status: 'healthy' | 'unhealthy';
  service: string;
  timestamp: number;
}

// ==================== API Functions ====================

export const aiApi = {
  // Luna - Staff Chat (Web Interface)
  chat: async (patientId: string, request: LunaChatRequest): Promise<LunaChatResponse> => {
    const response = await apiClient.post<LunaChatResponse>(
      `/v1/ai/luna/staff/chat`,
      {
        patientId,
        message: request.message,
        conversationId: request.conversationId || null,
      }
    );
    return response.data;
  },

  // Get AI-generated patient summary
  getPatientSummary: async (patientId: string): Promise<PatientSummaryResponse> => {
    const response = await apiClient.get<PatientSummaryResponse>(
      `/v1/ai/luna/patient/${patientId}/summary`
    );
    return response.data;
  },

  checkIn: async (patientId: string, checkInType?: string): Promise<LunaCheckInResponse> => {
    const response = await apiClient.post<LunaCheckInResponse>(
      `/v1/ai/luna/check-in/${patientId}`,
      null,
      { params: { checkInType: checkInType || 'general' } }
    );
    return response.data;
  },

  // Propensity
  getPropensityScore: async (patientId: string): Promise<PropensityScoreResponse> => {
    const response = await apiClient.get<PropensityScoreResponse>(
      `/v1/ai/propensity/${patientId}`
    );
    return response.data;
  },

  getBatchPropensityScores: async (patientIds: string[]): Promise<PropensityScoreResponse[]> => {
    const response = await apiClient.post<PropensityScoreResponse[]>(
      '/v1/ai/propensity/batch',
      { patientIds }
    );
    return response.data;
  },

  // Predictions
  getAllPredictions: async (patientId: string): Promise<AllPredictionsResponse> => {
    const response = await apiClient.get<AllPredictionsResponse>(
      `/v1/ai/predictions/${patientId}`
    );
    return response.data;
  },

  getHospitalizationPrediction: async (patientId: string): Promise<HospitalizationPrediction> => {
    const response = await apiClient.get<HospitalizationPrediction>(
      `/v1/ai/predictions/${patientId}/hospitalization`
    );
    return response.data;
  },

  getChurnPrediction: async (patientId: string): Promise<ChurnPrediction> => {
    const response = await apiClient.get<ChurnPrediction>(
      `/v1/ai/predictions/${patientId}/churn`
    );
    return response.data;
  },

  // Health
  healthCheck: async (): Promise<AIHealthResponse> => {
    const response = await apiClient.get<AIHealthResponse>('/v1/ai/health');
    return response.data;
  },
};
